import { useEffect, useState } from "react";
import {
  Button, Card, Box, Typography, Fade, Zoom, Grow,
  Chip, Paper, Grid, Tooltip, Alert
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ViewJobPlan from "./ViewJobPlan";
import LogoutButton from "./LogoutButton";
import ShiftRemarksButton from "./shiftRemarksButton";
import SettingsIcon from "@mui/icons-material/Settings";
import WorkIcon from "@mui/icons-material/Work";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import "./UserLogin.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function MainCategories() {
  const location = useLocation();
  const [username] = useState(
    location.state?.username || sessionStorage.getItem("username") || ""
  );
  const [formVisible, setFormVisible] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [fadeReady, setFadeReady] = useState(false);
  const [machineCode, setMachineCode] = useState(
    sessionStorage.getItem("machine_code") || ""
  );
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setFormVisible(true);
    const t1 = setTimeout(() => {
      setButtonsVisible(true);
      const t2 = setTimeout(() => setFadeReady(true), 50);
      return () => clearTimeout(t2);
    }, 300);
    return () => clearTimeout(t1);
  }, []);

  useEffect(() => {
    if (username) sessionStorage.setItem("username", username);
  }, [username]);

  // Verify the operator has a valid workstation assigned. If not, redirect
  // to login. We do this here so the user fails fast at the categories
  // page instead of clicking through to GeneralProcess / JobProcessSelection
  // and getting bounced from there.
  useEffect(() => {
    let redirectTimer;

    const verifyWorkstation = async () => {
      // Guard: missing username (direct URL hit / session lost).
      if (!username) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/getMachineCode?username=${username}`
        );
        const resolvedCode = response.data?.machineCode;

        if (!resolvedCode) {
          throw new Error("No workstation assigned to this user.");
        }

        sessionStorage.setItem("machine_code", resolvedCode);
        setMachineCode(resolvedCode);
        setError(null);
      } catch (err) {
        console.error("Workstation verification failed:", err);

        const status = err?.response?.status;
        let message =
          "Failed to verify workstation. Redirecting to login...";

        if (status === 401 || status === 403) {
          message = "Session expired. Redirecting to login...";
        } else if (status === 404) {
          message =
            "Workstation not found for this user. Redirecting to login...";
        } else if (err?.message === "Network Error") {
          message = "Network error. Redirecting to login...";
        }

        setError(message);

        // Session data intentionally NOT cleared — login page's
        // alterUserLogin flow uses localStorage.username to resume sessions.
        redirectTimer = setTimeout(() => {
          navigate("/", { replace: true });
        }, 2500);
      }
    };

    verifyWorkstation();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [username, navigate]);

  const handleClickGeneral = () => {
    if (!machineCode) return; // wait until verification completes
    navigate("/GeneralProcess", { state: { username, machine_code: machineCode } });
  };
  const handleClickJob = () => {
    if (!machineCode) return; // wait until verification completes
    navigate("/JobProcessSelection", { state: { username } });
  };

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert(
        "You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක."
      );
    };
    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, document.title, window.location.href);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  // Theme-aware CategoryCard (builds gradient from theme)
  const CategoryCard = ({
    onClick,
    icon,
    title,
    buttonText,
    description,
    delay = 0,
    reverse = false,
    backgroundTopics = []
  }) =>
    buttonsVisible && (
      <Grow in timeout={800} style={{ transitionDelay: `${delay}ms` }}>
        <Paper
          elevation={0}
          sx={{
            height: 250,
            borderRadius: "20px",
            background: (t) =>
              `linear-gradient(135deg, ${
                reverse ? t.palette.secondary.main : t.palette.primary.main
              } 0%, ${reverse ? t.palette.primary.main : t.palette.secondary.main} 100%)`,
            border: (t) => `1px solid ${alpha(t.palette.common.white, 0.2)}`,
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            p: 2
          }}
        >
          {/* Title */}
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mb: 2,
              color: "white",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              position: "relative",
              zIndex: 2
            }}
          >
            {title}
          </Typography>

          {/* Button (white – readable on both themes) */}
          <Button
            onClick={onClick}
            variant="contained"
            sx={{
              backgroundColor: (t) => alpha(t.palette.common.white, 0.92),
              color: (t) => t.palette.getContrastText("#fff"),
              fontWeight: "bold",
              borderRadius: "12px",
              px: 3,
              py: 1,
              mb: 2,
              transition: "all 0.3s",
              position: "relative",
              zIndex: 2,
              "&:hover": {
                backgroundColor: "white",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.2)"
              }
            }}
            endIcon={<PlayArrowIcon />}
          >
            {buttonText}
          </Button>

          {/* Topics chips */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1,
              mt: 1,
              zIndex: 2
            }}
          >
            {backgroundTopics.map((topic, index) => (
              <Chip
                key={index}
                label={topic}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: (t) => alpha(t.palette.common.white, 0.06),
                  color: "white",
                  borderColor: (t) => alpha(t.palette.common.white, 0.4),
                  fontWeight: 500,
                  backdropFilter: "blur(6px)"
                }}
              />
            ))}
          </Box>
        </Paper>
      </Grow>
    );

  return (
    <div className="banner">
      <Box
        sx={{
          p: 2,
          height: "100vh",
          maxHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Zoom in={formVisible} timeout={800}>
          <Card
            sx={{
              width: "100%",
              maxWidth: "1000px",
              height: "100%",
              maxHeight: "600px",
              borderRadius: "20px",
              backgroundColor: (t) => alpha(t.palette.background.paper, 0.96),
              backdropFilter: "blur(20px)",
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden"
            }}
          >
            {/* Header */}
            <Box
              sx={{
                textAlign: "center",
                p: 2.5,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.04)
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2
                }}
              >
                {fadeReady && (
                  <Fade in timeout={1000}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <ShiftRemarksButton Employee_ID={username} />
                      <ViewJobPlan username={username} />
                    </Box>
                  </Fade>
                )}
                <Chip
                  label={`Welcome, ${username}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2
                }}
              >
                <DashboardIcon sx={{ fontSize: 34, color: (t) => t.palette.primary.main }} />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    background: (t) =>
                      `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
                  }}
                >
                  Process Selection Dashboard
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, p: 3 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    border: (t) => `1px solid ${alpha(t.palette.error.main, 0.3)}`,
                  }}
                >
                  {error}
                </Alert>
              )}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: (t) => t.palette.primary.main
                          }}
                        >
                          General Process Topics:
                        </Typography>
                        {[
                          "Daily Maintenance",
                          "General Cleaning",
                          "Machine Break Down",
                          "No Work",
                          "Machine Service"
                        ].map((topic, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
                          >
                            • {topic}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
                          color: (t) => t.palette.text.primary,
                          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                          borderRadius: "12px",
                          border: (t) =>
                            `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
                          maxWidth: 300
                        }
                      }
                    }}
                  >
                    <div>
                      <CategoryCard
                        onClick={handleClickGeneral}
                        icon={<SettingsIcon />}
                        title="General Process"
                        buttonText="Start General"
                        description="Non job related process"
                        delay={0}
                        reverse={false}
                        backgroundTopics={[
                          "Daily Maintenance",
                          "General Cleaning",
                          "Machine Break Down",
                          "No Work",
                          "Machine Service"
                        ]}
                      />
                    </div>
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: (t) => t.palette.secondary.main
                          }}
                        >
                          Job Process Topics:
                        </Typography>
                        {[
                          "Planned Jobs",
                          "Lineup Jobs",
                          "Manually Entering Jobs"
                        ].map((topic, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
                          >
                            • {topic}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
                          color: (t) => t.palette.text.primary,
                          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                          borderRadius: "12px",
                          border: (t) =>
                            `1px solid ${alpha(t.palette.secondary.main, 0.2)}`,
                          maxWidth: 300
                        }
                      }
                    }}
                  >
                    <div>
                      <CategoryCard
                        onClick={handleClickJob}
                        icon={<WorkIcon />}
                        title="Job Process"
                        buttonText="Start Job"
                        description="Specific job assignments, custom operations, and specialized workflows"
                        delay={200}
                        reverse={true}
                        backgroundTopics={[
                          "Planned Jobs",
                          "Lineup Jobs",
                          "Manually Entering Jobs"
                        ]}
                      />
                    </div>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* OR badge */}
              {fadeReady && (
                <Fade in timeout={1000} style={{ transitionDelay: "400ms" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "60%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10
                    }}
                  >
                    <Paper
                      elevation={3}
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: (t) => t.palette.background.paper,
                        border: (t) => `2px solid ${t.palette.primary.main}`
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: (t) => t.palette.primary.main }}
                      >
                        OR
                      </Typography>
                    </Paper>
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2.5,
                borderTop: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => t.palette.action.hover,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Select a process type to begin your shift workflow
              </Typography>
              {fadeReady && (
                <Fade in timeout={1000} style={{ transitionDelay: "600ms" }}>
              <Box sx={{ paddingRight: 12 }}>
                    <LogoutButton username={username} />
                  </Box>
                </Fade>
              )}
            </Box>
          </Card>
        </Zoom>
      </Box>
    </div>
  );
}